/*
 * WHICH SITUATION THIS RECORD IS IN, AND WHAT THAT SITUATION SHOWS.
 *
 * The rebuilt record is one page with fourteen faces (Gabriel's mockup, frame 1e,
 * 2026-10-01): a new lead owed a first email reads nothing like a client the
 * morning after the win, and the old page's mistake was showing both the same
 * sections in the same order. So the page asks this module once, on load, which
 * situation it is in, and every section reads its place off SITUATION_MATRIX:
 * whether it LEADS (it is the lead block), is shown OPEN, is a one-line SUMMARY,
 * or is ABSENT. The shell computes this once and passes it down; no component
 * decides its own visibility from raw columns, because fourteen components each
 * re-deriving "is this a client" is fourteen chances to disagree.
 *
 * THE MATRIX IS DATA, PINNED BY A TEST. SITUATION_RAW below is frame 1e's own
 * table (SituationMatrix.dc.html), copied cell for cell, and
 * test/record-situation.test.js holds a second copy of the frame's literal: the
 * layout cannot drift from what he drew without a test saying so.
 *
 * THE RESOLVER IS THE BUILD MAP'S (scratchpad/check/buildmap.txt, RESOLVER): the
 * first rule that matches wins, then four overlays are read on top (a reply owed,
 * a hold, a meeting today or tomorrow, changes waiting in Approvals). Its inputs
 * are the three payloads the shell already holds:
 *   contact  GET /contacts/:id
 *   company  GET /companies/:id (status_history, pipeline_stints, lead_score)
 *   act      GET /companies/:id/actions (actions, cadence, thresholds, facts,
 *            suppression, pending, document, onboarding, lost_evidence, my_zone)
 * Pure: `now` is a parameter, so the tests pin the day.
 */
import { daysBetween, addDays, shortDate, relativeDay, clock, plural, fullName, toDate } from "./format.mjs";

/* ------------------------------------------------------------------ the matrix */

/** The fourteen situations, in the frame's column order. */
export const SITUATIONS = [
  "new_first_email",
  "new_chasing",
  "call_booked",
  "call_held",
  "pricing_sent",
  "deciding",
  "agreement",
  "new_client",
  "client",
  "churned",
  "parked",
  "lost",
  "out",
  "associated",
];

/** The column heads, word for word. */
export const SITUATION_LABELS = {
  new_first_email: "New · first email",
  new_chasing: "New · chasing",
  call_booked: "Call booked",
  call_held: "Call held",
  pricing_sent: "Pricing sent",
  deciding: "Deciding internally",
  agreement: "Agreement + legal",
  new_client: "New client",
  client: "Client",
  churned: "Churned",
  parked: "Parked",
  lost: "Lost",
  out: "Out",
  associated: "Associated contact",
};

/** The twelve rows, in the frame's order. */
export const SECTIONS = [
  "header",
  "next",
  "lead_block",
  "latest_conversation",
  "story",
  "score",
  "people",
  "deal",
  "linkedin",
  "timeline",
  "profile",
  "behind_scenes",
];

export const SECTION_LABELS = {
  header: "Header",
  next: "Next",
  lead_block: "Lead block",
  latest_conversation: "Latest conversation",
  story: "Story",
  score: "Score",
  people: "People",
  deal: "Deal and money",
  linkedin: "Their LinkedIn and intake",
  timeline: "Timeline",
  profile: "Profile",
  behind_scenes: "Behind the scenes",
};

export const STATES = { L: "leads", O: "open", S: "summary", A: "absent" };

/*
 * FRAME 1e, CELL FOR CELL. One string per situation, in SITUATIONS order; the
 * first letter is the state (L leads, O open, S summary, A absent) and the rest
 * is the cell's own line. Kept in the frame's shape on purpose, so a diff against
 * SituationMatrix.dc.html is a diff of two identical tables.
 */
export const SITUATION_RAW = {
  header: ["S New · score featured", "S New · score featured", "S Call scheduled · score featured", "S In progress · score small", "S Pricing sent · quiet n days", "S Deciding internally · score small", "S Agreement + legal · sub-state slot", "S Closed won · no signed copy yet · no score", "S Client since … · no score", "S Churned on … · no score", "S Parked (hatch) · wakes … · score featured", "S Closed lost · parks itself on …", "S One plain line", "S Associated at {company} · POC →"],
  next: ["O Write the first email", "O Nudge / Chase · last rung: Remove from pipeline", "O Invite sent? hint · Prep sits in the lead block", "O Follow up · Held / No-show · update stage?", "O Chase when quiet · owed", "O Reply · owed · Check in", "O Reply on the agreement · owed · Approvals", "O Write the welcome · Write the finance intro", "O Reply · owed", "A No sales actions", "O Re-engage (Proposed) · Move the wake-up · Stop reminders", "A No sales actions", "A", "S The company’s Next"],
  lead_block: ["L Form answers and message, dated · score reason · their time now", "L Touch 2 of 3 · our last email · next rung", "L The call in both zones · join · reminders · prep pack · last time", "L Every call, newest open · to-dos ours / theirs · promised", "L Quote verbatim · sent · opened n · their budget · days quiet · builder", "L Quote and their reply · days since · owed · next meeting", "L PandaDoc state · who’s on the paper · latest redline · quote one click", "L Closed won status · fee · onboarding checklist", "L Signed fee · since · AM · ad account (unlinked first)", "L When, why, tenure, fee, notice · “not recorded”", "L Why · since · wakes · last time · last message each way · score", "L When, why · quote · last call notes · parks itself in 90 days", "L One plain line", "L Pointer to the POC, then the company record"],
  latest_conversation: ["A None yet", "O Our last email, unanswered", "O Their last message", "O Their last message", "O Their last message", "S Inside the lead block", "O Leads when a reply is owed", "O Their last message · no Nudge", "O Their last message", "S Read-only", "S Inside the lead block", "S Read-only · no Nudge", "A", "A"],
  story: ["A None yet", "S Only with 3+ entries", "O", "O", "O", "O", "O", "O “before the welcome”", "O", "O", "O", "O", "A", "A"],
  score: ["L Featured · breakdown on click", "L Featured", "L Featured", "S Small", "S Small · Fit collapses to the badge", "S Small", "S Small", "A Fit: would score n today", "A Fit: would score n today", "A Fit: would score n today", "L Featured, in the lead block", "A Fit: would score n today", "A", "S Scored on {POC}"],
  people: ["S One person · Add a person", "S", "S Attendees in the prep pack", "O Who was on the call", "S", "S", "O Roles on the deal (Proposed)", "O POC · duplicate warning · referrer", "S POC · AM", "S", "S", "S", "S", "O Their place at the company"],
  deal: ["A", "A", "S Their budget, if told", "S Their budget from the call", "O First in the rail · quote kept", "O First in the rail", "O First in the rail · PandaDoc email ↗", "O Budget: is this our retainer?", "S Fee is in the lead block", "S Fee is in the lead block", "A", "S Quote kept", "A", "A"],
  linkedin: ["O Ad account · attribution", "S", "S Their ads in the prep pack", "S", "S", "S", "S", "S No ad account linked · Link (Proposed)", "O First in the rail · our ads’ reach", "S", "S", "S", "A", "A"],
  timeline: ["S The form, one entry", "O", "O", "O Calls expand to summary", "O", "O", "O Everyone · redlines from anyone", "O Everyone · stage moves with evidence", "O", "O", "O", "O", "S", "O Company-wide"],
  profile: ["S Tab", "S Tab", "S Tab", "S Tab", "S Tab", "S Tab", "S Tab", "S Tab", "S Tab", "S Tab", "S Tab", "S Tab", "S Tab", "S Tab"],
  behind_scenes: ["S Auto-reply: ran or why not · booking link · audited", "S Booking link · audited", "S Reminder plan (also beside the call)", "S", "S", "S", "S", "S Only what runs on clients", "S Only what runs on clients", "S", "S", "S", "A", "A"],
};

function parseCell(raw) {
  const s = String(raw);
  return { state: STATES[s[0]], line: s.length > 2 ? s.slice(2) : "" };
}

/** { [situation]: { [section]: { state, line } } }, built once from the frame. */
export const SITUATION_MATRIX = Object.freeze(
  Object.fromEntries(
    SITUATIONS.map((situation, i) => [
      situation,
      Object.freeze(Object.fromEntries(SECTIONS.map((section) => [section, Object.freeze(parseCell(SITUATION_RAW[section][i]))]))),
    ])
  )
);

/** One cell: { state, line }. Absent for anything the matrix does not name. */
export function cell(situation, section) {
  const row = SITUATION_MATRIX[situation];
  return (row && row[section]) || { state: "absent", line: "" };
}

/** The state alone: "leads" | "open" | "summary" | "absent". */
export function stateOf(situation, section) {
  return cell(situation, section).state;
}

/* ------------------------------------------------------------------ helpers */

// The stages in board order (src/db/crm.js PIPELINE_STAGES; test/stage-order.test.js).
const BOARD_LABELS = {
  new: "New",
  call_scheduled: "Call scheduled",
  in_progress: "In progress",
  proposal_sent: "Pricing sent",
  evaluating: "Deciding internally",
  agreement: "Agreement + legal",
  closed_won: "Closed won",
  closed_lost: "Closed lost",
};
export const STAGE_LABELS = BOARD_LABELS;

const STAGE_SITUATION = {
  call_scheduled: "call_booked",
  in_progress: "call_held",
  proposal_sent: "pricing_sent",
  evaluating: "deciding",
  agreement: "agreement",
};

// The day a new client stops being new, and the win from which the finance
// introduction is owed (src/actions/model.js FINANCE_INTRO_FROM).
export const NEW_CLIENT_DAYS = 30;
export const FINANCE_INTRO_FROM = "2026-09-01T00:00:00Z";
// recycle.js LOST_TO_RECYCLED_DAYS: a loss turns into a park this many days on.
export const LOST_TO_PARK_DAYS = 90;
// pricing_stale's threshold, for "quiet n days" when the payload has none.
const PRICING_QUIET_DAYS = 14;

const OUT_STATUSES = new Set(["vendor", "advisory", "not_a_fit"]);
const OUT_LABELS = { vendor: "Vendor", advisory: "Advisory", not_a_fit: "Not a fit" };

/** The latest status_history row that moved `field` to `to`, or null. */
export function lastTransition(company, field, to) {
  const rows = ((company && company.status_history) || []).filter(
    (r) => r && r.field === field && (to === undefined || r.to_value === to)
  );
  if (!rows.length) return null;
  return rows.reduce((best, r) => (toDate(r.occurred_at) > toDate(best.occurred_at) ? r : best));
}

const hasKind = (actions, kinds) => (actions || []).some((a) => a && kinds.includes(a.kind));

/** Who set a status, in the timeline's words: "set by you", "automatic". */
export function setByWords(row) {
  if (!row) return null;
  switch (row.source) {
    case "app":
      return "set by you";
    case "mcp":
      return "set through Claude";
    case "automation":
    case "auditor":
      return "automatic";
    case "hubspot_import":
      return "from the HubSpot import";
    default:
      return null;
  }
}

/** The signed copy on record: a pasted link, a signed notice, or a PandaDoc signature. */
export function hasSignedCopy(company, act) {
  if (company && company.signed_agreement_url) return true;
  if (company && company.agreement_notice && company.agreement_notice.url) return true;
  return !!(act && act.document && act.document.signed_at);
}

/*
 * THE SOURCE PILL: "Referral · intro email · Dani Avitz", "LinkedIn · Get a
 * proposal form", "Organic · form". It reads the STORED source (contacts.
 * lead_source) and never guesses one: a contact with no source gets no pill, as
 * drawn, because a wrong pill is worse than none (ui.js sourcePill). The variant
 * names the one colour each source may wear: blue LinkedIn, pink referral, and
 * organic a black outline.
 */
const SOURCE_WORDS = { linkedin: "LinkedIn", referral: "Referral", organic: "Organic" };
const CHANNEL_WORDS = { website_form: "form", intro_email: "intro email", linkedin_dm: "LinkedIn DM", whatsapp_dm: "WhatsApp DM", other: "other" };

export function sourcePill(contact, { formName = null } = {}) {
  const source = contact && contact.lead_source;
  if (!SOURCE_WORDS[source]) return null;
  const channel = contact.lead_channel || contact.lead_channel_derived || null;
  const parts = [SOURCE_WORDS[source]];
  if (channel === "website_form" && formName) parts.push(/form$/i.test(formName) ? formName : `${formName} form`);
  else if (CHANNEL_WORDS[channel]) parts.push(CHANNEL_WORDS[channel]);
  if (source === "referral" && contact.referrer_name) parts.push(contact.referrer_name);
  return { text: parts.join(" · "), variant: source };
}

/** The point of contact, from GET /companies/:id. */
export function pointOfContact(company) {
  if (!company) return null;
  if (company.poc) return company.poc;
  const id = company.primary_contact_id;
  return ((company.contacts || []).find((c) => String(c.id) === String(id))) || null;
}

/* ------------------------------------------------------------------ resolver */

/**
 * THE SITUATION, read from the payloads. Returns
 *
 *   {
 *     key,                 one of SITUATIONS
 *     variant,             for 'out': no_company | not_a_lead | vendor | advisory |
 *                          not_a_fit | not_on_board; else null
 *     company_situation,   for 'associated': what the company itself is in, so the
 *                          company's record can follow the pointer
 *     days_in_stage,       d(facts.stage_entered_at, now), or null
 *     since,               the date the current status or stage began, or null
 *     wakes_at,            parked: recycle_until; lost: when the park will wake
 *     parks_at,            lost: lost_at + 90 days, the day it parks itself
 *     lost_at, churned_at, won_at, quiet_days, won_day,
 *     signed,              a signed copy is on record
 *     overlays: { reply_owed, hold, meeting, pending }
 *   }
 *
 * Never throws on a partial payload: a record still loading its actions resolves
 * from the company alone, and a contact with no company is 'out'.
 */
export function resolveSituation({ contact = null, company = null, act = null, now = new Date() } = {}) {
  const base = resolveCompany({ contact, company, act, now });
  if (base.key === "out" && (base.variant === "no_company" || base.variant === "not_a_lead")) return base;

  // Rule 2: an associated colleague is a pointer to the point of contact, with
  // the company's own record following it. The company's situation is resolved
  // anyway, because "the company record follows below" needs to know which one.
  if (contact && contact.kind === "associated") {
    return { ...base, key: "associated", variant: null, company_situation: base.key, company_variant: base.variant };
  }
  return base;
}

function resolveCompany({ contact, company, act, now }) {
  const facts = (act && act.facts) || {};
  const actions = (act && act.actions) || [];
  const out = {
    key: null,
    variant: null,
    company_situation: null,
    days_in_stage: daysBetween(facts.stage_entered_at, now),
    since: null,
    wakes_at: null,
    parks_at: null,
    lost_at: null,
    churned_at: null,
    won_at: null,
    won_day: null,
    quiet_days: null,
    signed: hasSignedCopy(company, act),
    overlays: overlaysOf({ company, act }),
  };

  // Rule 1: a junk contact is never a lead, whatever its company; and a person
  // with no company has no pipeline position to show at all.
  if (contact && contact.kind === "junk") return { ...out, key: "out", variant: "not_a_lead" };
  if (!company || (contact && !contact.company_id)) return { ...out, key: "out", variant: "no_company" };

  const status = company.status;

  // Rule 3: the company's relationship decides, before its stage does.
  if (OUT_STATUSES.has(status)) {
    const row = lastTransition(company, "status", status);
    return { ...out, key: "out", variant: status, since: row ? row.occurred_at : null };
  }
  if (status === "churned") {
    return { ...out, key: "churned", churned_at: company.churned_at || null, won_at: company.won_at || null, since: company.churned_at || null };
  }
  if (status === "lost") {
    const row = lastTransition(company, "status", "lost");
    const lostAt = (row && row.occurred_at) || company.updated_at || null;
    const parksAt = lostAt ? addDays(lostAt, LOST_TO_PARK_DAYS) : null;
    // recycle.js parks a loss 90 days on whatever re_engage_after says; the date he
    // set becomes the park's wake-up, but only while it is still ahead of the turn.
    const reEngage = toDate(company.re_engage_after);
    const wakes = reEngage && parksAt && reEngage > parksAt ? reEngage : parksAt;
    return { ...out, key: "lost", lost_at: lostAt, parks_at: parksAt, wakes_at: wakes, since: lostAt };
  }
  if (status === "recycled") {
    const row = lastTransition(company, "status", "recycled");
    return { ...out, key: "parked", since: row ? row.occurred_at : null, wakes_at: company.recycle_until || null };
  }
  if (status === "client") {
    const wonDay = daysBetween(company.won_at, now);
    const onboarding = (act && act.onboarding) || {};
    const welcomeDone = !!(onboarding.welcome || facts.kickoff_sent_at);
    const financeApplies =
      onboarding.finance_applies !== undefined
        ? !!onboarding.finance_applies
        : !!company.won_at && toDate(company.won_at) >= toDate(FINANCE_INTRO_FROM);
    const financeDone = !financeApplies || !!(onboarding.finance_intro || facts.finance_intro_sent_at);
    const fresh = company.won_at && wonDay !== null && wonDay >= 0 && wonDay <= NEW_CLIENT_DAYS;
    const key = fresh && !(welcomeDone && financeDone) ? "new_client" : "client";
    return { ...out, key, won_at: company.won_at || null, won_day: wonDay, since: company.won_at || null };
  }

  // A prospect. Off the board altogether is its own plain line.
  const stage = company.pipeline_stage;
  if (!stage) return { ...out, key: "out", variant: "not_on_board", since: company.created_at || null };
  if (stage === "new") {
    const untouched = act && act.cadence ? act.cadence.state === "untouched" : !facts.last_outbound_at;
    return { ...out, key: untouched ? "new_first_email" : "new_chasing", since: facts.stage_entered_at || company.created_at || null };
  }
  if (stage === "closed_won") return { ...out, key: "client", won_at: company.won_at || null };
  if (stage === "closed_lost") return { ...out, key: "lost" };
  const key = STAGE_SITUATION[stage] || "new_chasing";
  const quiet =
    key === "pricing_sent" ? daysBetween(facts.last_activity_at || facts.stage_entered_at, now) : null;
  return { ...out, key, since: facts.stage_entered_at || null, quiet_days: quiet };
}

/*
 * THE FOUR OVERLAYS, read after the situation (build map, "Overlays"):
 *   reply_owed  they spoke last and it is our turn. Latest conversation becomes the
 *               first lead block, with the pulsing red dot. The model never raises
 *               this for a client or a churned company, so neither does this.
 *   hold        a snooze or an ignore Gabriel chose (not the derived kinds), which
 *               hatches the position pill and puts a state line in Next.
 *   meeting     a meeting today or tomorrow: a status line with Prep in the lead
 *               block, not a Next line.
 *   pending     changes waiting in Approvals for this company or its people.
 */
export function overlaysOf({ company = null, act = null } = {}) {
  const facts = (act && act.facts) || {};
  const actions = (act && act.actions) || [];
  const live = !company || (company.status !== "client" && company.status !== "churned");
  const replyOwed = live && (hasKind(actions, ["reply_waiting", "agreement_reply"]) || facts.reply_owed === true);

  const s = act && act.suppression;
  const hold =
    s && !s.derived && (s.kind === "snoozed" || s.kind === "ignored")
      ? { kind: s.kind, until: s.until || null, until_reply: !!s.until_reply, note: s.note || null, reason_label: s.reason_label || null, forever: !!s.forever }
      : null;

  const m = actions.find((a) => a && (a.kind === "meeting_today" || a.kind === "meeting_soon"));
  const meeting = m
    ? { kind: m.kind, at: facts.next_meeting_at || m.at || null, title: facts.next_meeting_title || null, contact_id: m.contact_id || null }
    : null;

  const pending = Array.isArray(act && act.pending) ? act.pending.length : Number(facts.pending_count) || 0;
  return { reply_owed: !!replyOwed, hold, meeting, pending };
}

/* ---------------------------------------------------------------- the header */

/*
 * THE POSITION LINE: the pill (which opens Change status…) and the words beside it.
 *
 *   { pill, variant, text, subState, hatch }
 *
 * `variant` is the pill's treatment, never a colour chosen here: 'stage' (black
 * fill, a live prospect), 'client' (green, won), 'outline' (every other status),
 * 'hatch' (parked, or anything on hold). Words are the board's (ui.js
 * BOARD_STAGE_LABELS) and the frame's, and "Parked" never "recycled".
 */
export function positionLine(resolved, { company = null, now = new Date(), timeZone } = {}) {
  const r = resolved || {};
  const co = company || {};
  const d = (ts) => shortDate(ts, { now, timeZone });
  const inStage = r.days_in_stage === null || r.days_in_stage === undefined ? null : r.days_in_stage === 0 ? "entered today" : `${plural(r.days_in_stage, "day")} in stage`;
  const hold = r.overlays && r.overlays.hold;
  const base = (() => {
    switch (r.key) {
      case "new_first_email":
      case "new_chasing": {
        const at = r.since || co.created_at;
        const day = relativeDay(at, { now, timeZone });
        return { pill: "New", variant: "stage", text: at ? `arrived ${day} · ${clock(at, { timeZone })}` : null };
      }
      case "call_booked":
        return { pill: "Call scheduled", variant: "stage", text: inStage };
      case "call_held":
        return { pill: "In progress", variant: "stage", text: inStage };
      case "pricing_sent":
        return {
          pill: "Pricing sent",
          variant: "stage",
          text: r.quiet_days === null || r.quiet_days === undefined ? inStage : r.quiet_days === 0 ? "active today" : `quiet ${plural(r.quiet_days, "day")}`,
          quietDays: r.quiet_days,
          quiet: (r.quiet_days || 0) >= PRICING_QUIET_DAYS,
          // The sub-states (B-008) are defined for Agreement and Pricing sent
          // (reality.txt [25]); the header's slot reads the same rule.
          subState: true,
        };
      case "deciding":
        return { pill: "Deciding internally", variant: "stage", text: inStage };
      case "agreement":
        return { pill: "Agreement + legal", variant: "stage", text: inStage, subState: true };
      case "new_client": {
        const wonText = r.won_day === 0 ? "won today" : `day ${r.won_day}`;
        return {
          pill: r.signed ? "Closed won" : "Closed won · no signed copy yet",
          variant: "client",
          text: r.won_at ? `Client since ${d(r.won_at)} · ${wonText}` : null,
        };
      }
      case "client":
        return { pill: "Client", variant: "client", text: r.won_at ? `since ${d(r.won_at)}` : null };
      case "churned":
        return { pill: "Churned", variant: "outline", text: r.churned_at ? `on ${d(r.churned_at)}` : null };
      case "parked": {
        const wakes = r.wakes_at ? (daysBetween(now, r.wakes_at) <= 0 ? "today" : d(r.wakes_at)) : null;
        const parkedFor = r.since ? daysBetween(r.since, now) : null;
        return {
          pill: wakes ? `Parked · wakes ${wakes}` : "Parked · no reminder",
          variant: "hatch",
          text: parkedFor === null ? null : `parked ${plural(parkedFor, "day")}`,
        };
      }
      case "lost":
        return { pill: "Closed lost", variant: "outline", text: r.parks_at ? `parks itself on ${d(r.parks_at)}` : null };
      case "out":
        return outPosition(r, { company: co, now, timeZone });
      case "associated":
        return { pill: null, variant: null, text: null };
      default:
        return { pill: null, variant: null, text: null };
    }
  })();
  return { subState: false, ...base, hatch: base.variant === "hatch" || !!hold };
}

function outPosition(r, { now, timeZone }) {
  const d = (ts) => shortDate(ts, { now, timeZone });
  switch (r.variant) {
    case "no_company":
      return { pill: "No company", variant: "outline", text: null };
    case "not_a_lead":
      return { pill: "Not a lead", variant: "outline", text: null };
    case "not_on_board":
      return { pill: "Not on the board", variant: "outline", text: r.since ? `arrived ${d(r.since)}` : null };
    default:
      return { pill: OUT_LABELS[r.variant] || "Out", variant: "outline", text: r.since ? `since ${d(r.since)}` : null };
  }
}

/**
 * THE ONE PLAIN LINE for 'out' (frame 1i, "Out and associated"), with its one
 * action: "Vendor · computer software · since Jun 12", "No company · Link to a
 * company", "Not on the board · arrived Sep 29 · Add to the board". The vendor
 * line names the industry when one is stored (there is no vendor field), and a
 * not-a-fit line its reason in words.
 */
export function outLine(resolved, { company = null, now = new Date(), timeZone } = {}) {
  const r = resolved || {};
  const co = company || {};
  const d = (ts) => shortDate(ts, { now, timeZone });
  const since = r.since ? `since ${d(r.since)}` : null;
  switch (r.variant) {
    case "no_company":
      return { text: "No company", action: { id: "link_company", label: "Link to a company" } };
    case "not_a_lead":
      return { text: "Not a lead", action: null };
    case "not_on_board":
      return {
        text: ["Not on the board", r.since ? `arrived ${d(r.since)}` : null].filter(Boolean).join(" · "),
        action: { id: "add_to_board", label: "Add to the board" },
      };
    case "vendor": {
      const industry = co.industry ? String(co.industry).replace(/_/g, " ").toLowerCase() : null;
      return { text: ["Vendor", industry, since].filter(Boolean).join(" · "), action: null };
    }
    case "not_a_fit": {
      const why = co.status_reason ? String(co.status_reason).replace(/_/g, " ") : null;
      return { text: ["Not a fit", why, since].filter(Boolean).join(" · "), action: null };
    }
    case "advisory":
      return { text: ["Advisory", since].filter(Boolean).join(" · "), action: null };
    default:
      return { text: "Out of the pipeline", action: null };
  }
}

/** "Associated at Pluto · point of contact Shahar Bahat →", with who to open. */
export function associatedLine(company) {
  const poc = pointOfContact(company);
  return {
    company: (company && company.name) || null,
    poc_id: poc ? poc.id : null,
    poc_name: poc ? fullName(poc) : null,
    text: `Associated at ${(company && company.name) || "this company"}`,
  };
}

/* ------------------------------------------------------------------ the score */

/*
 * HOW PROMINENT THE SCORE IS (frame 1e, Score row):
 *   featured   the header badge with its band segments, and a reason line in the
 *              lead block: new leads, a booked call, a parked company;
 *   small      the badge alone, mid-deal;
 *   fit_only   gone from the header, kept in Fit as "Would score n today":
 *              clients, churned, lost;
 *   scored_on  an associated colleague: the company's score, said to be someone
 *              else's ("Scored on Shahar Bahat");
 *   absent     out.
 */
export function scoreProminence(situation) {
  switch (situation) {
    case "new_first_email":
    case "new_chasing":
    case "call_booked":
    case "parked":
      return "featured";
    case "call_held":
    case "pricing_sent":
    case "deciding":
    case "agreement":
      return "small";
    case "new_client":
    case "client":
    case "churned":
    case "lost":
      return "fit_only";
    case "associated":
      return "scored_on";
    default:
      return "absent";
  }
}

/* ------------------------------------------------------------------ the rail */

/*
 * FIT IS NOT A ROW OF THE MATRIX (it lives inside Score there), so its state is
 * here: open where the fit is the question (a new lead: frame 1c draws it open),
 * a summary everywhere else, absent when the record is out.
 */
const FIT_STATE = {
  new_first_email: "open",
  new_chasing: "open",
  out: "absent",
};

/*
 * THE RAIL'S ORDER, by situation (the brief: "Fit first for a new lead, Deal first
 * from Pricing sent, Account first for a client"; build map, "Rail order"). Each
 * entry carries the state the matrix gives that group, and absent groups are left
 * out, so the caller renders the list as it comes.
 */
const RAIL_ORDERS = {
  fit_first: ["fit", "people", "deal", "linkedin", "behind_scenes"],
  deal_first: ["deal", "people", "fit", "linkedin", "behind_scenes"],
  account_first: ["linkedin", "deal", "people", "fit", "behind_scenes"],
  parked: ["fit", "people", "linkedin", "behind_scenes"],
  out: ["people"],
  associated: ["people", "fit"],
};

const RAIL_FOR = {
  new_first_email: "fit_first",
  new_chasing: "fit_first",
  call_booked: "fit_first",
  call_held: "fit_first",
  pricing_sent: "deal_first",
  deciding: "deal_first",
  agreement: "deal_first",
  new_client: "deal_first",
  client: "account_first",
  churned: "deal_first",
  parked: "parked",
  lost: "deal_first",
  out: "out",
  associated: "associated",
};

/*
 * THE FULL PAGE HAS ROOM, SO MORE OF THE RAIL STARTS OPEN. The matrix's states are
 * the panel's: 560 wide, a group that opens pushes the timeline a screen down, so
 * most groups are one line. At 1440 the rail is its own sticky column beside the
 * main one, and Gabriel drew it open: frame 1b (Pluto, new client) opens Fit
 * ("Would score 33 · medium · Breakdown", the firmographics, Edit) and Their
 * LinkedIn and intake (the dashed "No ad account linked", Intake, Attribution);
 * frame 1d (Northwind, full page) opens Their LinkedIn and intake too, but keeps
 * Fit closed, because mid-deal the score is "Small" (the matrix's Score row) and
 * the fit is no longer the question. Behind the scenes stays a line everywhere.
 */
function fullPageState(situation, group, state) {
  if (state !== "summary") return state;
  if (group === "linkedin") return "open";
  if (group === "fit" && scoreProminence(situation) !== "small") return "open";
  return state;
}

/**
 * [{ group, state, line }] for the rail, in order, absent groups left out.
 * `layout` is the shell's ("full" | "panel" | "phone"): the full page opens what
 * frames 1b and 1d draw open (fullPageState); the panel and the phone read the
 * matrix as it is.
 */
export function railOrder(situation, { layout = null } = {}) {
  const order = RAIL_ORDERS[RAIL_FOR[situation]] || RAIL_ORDERS.fit_first;
  const full = layout === "full";
  return order
    .map((group) => {
      if (group === "fit") {
        const state = FIT_STATE[situation] || "summary";
        return { group, state: full ? fullPageState(situation, group, state) : state, line: cell(situation, "score").line };
      }
      const c = cell(situation, group);
      // The matrix marks People "summary" for out, and the rail still carries it:
      // a vendor's people are the one thing left worth reading.
      return { group, state: full ? fullPageState(situation, group, c.state) : c.state, line: c.line };
    })
    .filter((g) => g.state !== "absent");
}

/* -------------------------------------------------------- the main column */

/**
 * The main column's sections after the header, in order:
 *   ['next', 'latest_conversation', 'lead_block', 'story'] and so on.
 *
 * A reply owed puts Latest conversation first among the blocks, whatever the
 * situation (the frame's own rule: "A reply owed always makes Latest
 * conversation the lead block"). Where the matrix says the conversation sits
 * "inside the lead block" (Deciding internally, Parked) it is not a section of
 * its own. Absent sections are left out.
 */
export function mainSections(resolved, { entries = 0 } = {}) {
  const r = resolved || {};
  const key = r.key;
  const owed = !!(r.overlays && r.overlays.reply_owed);
  const out = [];
  // Changes waiting in Approvals reach the record through Next, so Next is there
  // whenever something waits, even where the matrix has no Next at all: the
  // common "they wrote back" proposal lands on lost and not-a-fit companies, and
  // Approvals opens exactly those with the pending line on top. NextList then
  // draws only that line (nextModel makes no sales lines for these statuses).
  if (stateOf(key, "next") !== "absent" || Number(r.overlays && r.overlays.pending) > 0) out.push("next");
  // A person with no company is one plain line in the matrix, and still a person
  // who wrote: "are you taking on new clients?" has to be readable, and
  // answerable, from their record. Every other Out keeps the matrix's absent.
  const convo = key === "out" && r.variant === "no_company" ? "open" : stateOf(key, "latest_conversation");
  const convoAlone = convo !== "absent" && !/^Inside the lead block/.test(cell(key, "latest_conversation").line);
  if (owed && convo !== "absent") out.push("latest_conversation");
  out.push("lead_block");
  if (!owed && convoAlone) out.push("latest_conversation");
  if (showStory(key, entries)) out.push("story");
  return out;
}

/** The story needs three timeline entries, and some situations never show one. */
export function showStory(situation, entries = 0) {
  if (stateOf(situation, "story") === "absent") return false;
  return Number(entries) >= 3;
}

/**
 * What Behind the scenes may hold beside the automations: the booking link only
 * for a prospect or a parked company (a client has no use for a sales link), and
 * the "audited" flag only for a prospect past its first email. Frame 1c's
 * summary ends "· not audited" and the matrix's New · first email cell lists
 * "audited", but the brief's New lead row says "Gone: … audit", and the coverage
 * check ruled for the brief (coverage-situations [17]): before the first email
 * nobody has looked at their account, so "not audited" is noise on every new lead.
 * Parked is out too (its status is not a prospect's). An audit already written
 * stays readable in Profile's LinkedIn ads group whatever the situation.
 */
export function behindScenesShows(situation, company) {
  const status = company && company.status;
  return {
    booking: status === "prospect" || status === "recycled",
    audit: status === "prospect" && situation !== "new_first_email" && situation !== "out",
  };
}

/** Everything the shell computes once and passes down. */
export function recordView({ contact = null, company = null, act = null, now = new Date(), entries = 0, timeZone, layout = null } = {}) {
  const situation = resolveSituation({ contact, company, act, now });
  const key = situation.key;
  /*
   * AN ASSOCIATED COLLEAGUE IS A POINTER, THEN THE COMPANY'S RECORD (frame 1i). So the
   * sections below the pointer are the company's own: Ella owes a reply on
   * Northwind's agreement whoever's record it is opened through, and the reply leads.
   * People opens, because "their place at the company" is the question (the matrix's
   * Associated column); the score is the company's, said to be someone else's.
   */
  const via = key === "associated" && situation.company_situation && situation.company_situation !== "out" ? situation.company_situation : null;
  const rail = via
    ? railOrder(via, { layout }).map((g) => (g.group === "people" ? { ...g, state: "open", line: cell("associated", "people").line } : g))
    : railOrder(key, { layout });
  return {
    situation,
    key,
    matrix: SITUATION_MATRIX[via || key],
    position: positionLine(key === "associated" ? { ...situation, key: situation.company_situation, variant: situation.company_variant } : situation, { company, now, timeZone }),
    prominence: scoreProminence(key),
    rail,
    main: mainSections(via ? { ...situation, key: via } : situation, { entries }),
    behind: behindScenesShows(via || key, company),
  };
}

